// ── Recoil Kick Visualization ──
// Visual-only camera + viewmodel kick on weapon fire.
// Does NOT affect actual aim direction (server-authoritative).
//
// On fire:
//   a. Camera pitch: kick upward by weapon's recoil * category scale
//   b. Camera roll: slight random +-1 degree for shake feel
//   c. Viewmodel Z: push backward, recover over ~100ms
//   d. Viewmodel rotX: tilt up, recover over ~150ms
//
// Recovery uses a damped spring model (overshoots slightly, then settles).
// Sustained fire accumulates camera kick via a burst multiplier.

import { WEAPONS, type WeaponId } from '../core/config';

// ---------------------------------------------------------------------------
// Category scaling factors — per weapon type
// ---------------------------------------------------------------------------

const CATEGORY_SCALE: Record<string, number> = {
  pistol: 0.5,
  smg: 0.7,
  rifle: 1.0,
  sniper: 1.5,
  shotgun: 2.0,
  melee: 0.2,
};

// ---------------------------------------------------------------------------
// Base kick magnitudes (before category scaling)
// ---------------------------------------------------------------------------

/** Base camera pitch kick in radians (visual only). */
const BASE_CAMERA_PITCH = 0.015;

/** Max camera roll kick in radians (~1 degree). */
const MAX_CAMERA_ROLL = (1.0 * Math.PI) / 180;

/** Base viewmodel backward push (Z axis). */
const BASE_VM_POS_Z = 0.03;

/** Base viewmodel rotation tilt (X axis, radians, ~3.5 degrees). */
const BASE_VM_ROT_X = 0.06;

// ---------------------------------------------------------------------------
// Recovery parameters (damped spring)
// ---------------------------------------------------------------------------

/** Natural frequency of the spring (higher = faster oscillation). */
const SPRING_FREQUENCY = 25.0;

/** Damping ratio: < 1 = underdamped (overshoots), 1 = critical, > 1 = overdamped. */
const SPRING_DAMPING = 0.6;

/** Time without firing before burst counter resets (seconds). */
const BURST_RESET_TIME = 0.4;

/** How much each consecutive shot increases the burst multiplier. */
const BURST_INCREMENT = 0.12;

/** Maximum burst multiplier. */
const BURST_MAX_MULT = 2.5;

// ---------------------------------------------------------------------------
// Offsets returned each frame
// ---------------------------------------------------------------------------

export interface RecoilKickOffsets {
  /** Camera pitch offset in radians (positive = upward). Visual only. */
  cameraPitch: number;
  /** Camera roll offset in radians. Visual only. */
  cameraRoll: number;
  /** Viewmodel position offset on Z axis (positive = backward). */
  viewmodelPosZ: number;
  /** Viewmodel rotation offset on X axis in radians (positive = tilted up). */
  viewmodelRotX: number;
}

// ---------------------------------------------------------------------------
// Damped spring state for a single axis
// ---------------------------------------------------------------------------

interface SpringState {
  /** Current displacement from rest. */
  position: number;
  /** Current velocity. */
  velocity: number;
}

function springStep(s: SpringState, dt: number): void {
  // Damped harmonic oscillator: x'' = -w^2 * x - 2 * z * w * x'
  const w = SPRING_FREQUENCY;
  const z = SPRING_DAMPING;
  const accel = -w * w * s.position - 2 * z * w * s.velocity;
  s.velocity += accel * dt;
  s.position += s.velocity * dt;

  // Snap to zero when negligible
  if (Math.abs(s.position) < 0.00005 && Math.abs(s.velocity) < 0.001) {
    s.position = 0;
    s.velocity = 0;
  }
}

// ---------------------------------------------------------------------------
// RecoilKickSystem
// ---------------------------------------------------------------------------

export class RecoilKickSystem {
  // Spring states for each axis
  private pitchSpring: SpringState = { position: 0, velocity: 0 };
  private rollSpring: SpringState = { position: 0, velocity: 0 };
  private vmPosZSpring: SpringState = { position: 0, velocity: 0 };
  private vmRotXSpring: SpringState = { position: 0, velocity: 0 };

  // Sustained fire tracking
  private burstCount = 0;
  private timeSinceLastShot = Infinity; // seconds

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * Apply a visual recoil kick for the given weapon.
   * Call this each time the weapon fires.
   */
  applyKick(weaponId: WeaponId | string): void {
    const scale = this.getCategoryScale(weaponId);

    // Update burst tracking
    if (this.timeSinceLastShot < BURST_RESET_TIME) {
      this.burstCount++;
    } else {
      this.burstCount = 0;
    }
    this.timeSinceLastShot = 0;

    const burstMult = Math.min(1 + this.burstCount * BURST_INCREMENT, BURST_MAX_MULT);

    // Camera pitch — always kicks upward
    const pitchKick = BASE_CAMERA_PITCH * scale * burstMult;
    this.pitchSpring.position += pitchKick;
    this.pitchSpring.velocity += pitchKick * SPRING_FREQUENCY * 0.5;

    // Camera roll — random left/right
    const rollKick = MAX_CAMERA_ROLL * scale * (Math.random() > 0.5 ? 1 : -1);
    this.rollSpring.position += rollKick;
    this.rollSpring.velocity += rollKick * SPRING_FREQUENCY * 0.3;

    // Viewmodel backward push
    const posZKick = BASE_VM_POS_Z * scale;
    this.vmPosZSpring.position += posZKick;
    this.vmPosZSpring.velocity += posZKick * SPRING_FREQUENCY * 0.4;

    // Viewmodel rotation tilt
    const rotXKick = BASE_VM_ROT_X * scale;
    this.vmRotXSpring.position += rotXKick;
    this.vmRotXSpring.velocity += rotXKick * SPRING_FREQUENCY * 0.4;
  }

  /**
   * Update the spring recovery. Call once per frame.
   * @param dt Frame delta time in seconds
   */
  update(dt: number): void {
    this.timeSinceLastShot += dt;

    springStep(this.pitchSpring, dt);
    springStep(this.rollSpring, dt);
    springStep(this.vmPosZSpring, dt);
    springStep(this.vmRotXSpring, dt);
  }

  /**
   * Get the current recoil kick offsets. Apply these to camera and viewmodel each frame.
   */
  getOffsets(): RecoilKickOffsets {
    return {
      cameraPitch: this.pitchSpring.position,
      cameraRoll: this.rollSpring.position,
      viewmodelPosZ: this.vmPosZSpring.position,
      viewmodelRotX: this.vmRotXSpring.position,
    };
  }

  /**
   * Reset all offsets and state to zero (on respawn, weapon switch, etc).
   */
  reset(): void {
    this.pitchSpring = { position: 0, velocity: 0 };
    this.rollSpring = { position: 0, velocity: 0 };
    this.vmPosZSpring = { position: 0, velocity: 0 };
    this.vmRotXSpring = { position: 0, velocity: 0 };
    this.burstCount = 0;
    this.timeSinceLastShot = Infinity;
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private getCategoryScale(weaponId: WeaponId | string): number {
    const cfg = WEAPONS[weaponId as WeaponId];
    if (!cfg) return 1.0; // fallback for unknown weapons
    return CATEGORY_SCALE[cfg.type] ?? 1.0;
  }
}
